import { useState, type ReactNode } from 'react';
import { Num, T, Var, useGT } from 'gt-react';
import {
  ArrowClockwiseIcon,
  CaretDownIcon,
  CheckIcon,
  SpinnerGapIcon,
  WarningCircleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { CorrectionStrategy, ValidationRuleTrigger } from '@erp/shared-types';
import type {
  CatalogAdvancedFilter,
  ValidationRuleSchemaDTO,
  ValidationSchemaField,
} from '@erp/rpc';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { WizardField } from '@/components/ui/wizard';
import { cn } from '@/lib/utils';
import { AdvancedFilters } from '../../components/AdvancedFilters';
import { AssigneePicker } from '../../components/AssigneePicker';
import { EvaluationResultView } from '../components/EvaluationResultView';
import { formatScheduleTime } from '../components/validationRuleDisplay';
import { CRON_EXAMPLE, nextCronRun } from '../validationScheduleCron';
import { fieldKey } from './validationRuleWizardChecks';
import type {
  NormalizationFlag,
  ValidationRuleWizardState,
  WizardDivisionOption,
} from './useValidationRuleWizardState';

// Radix's RadioGroup hands `onValueChange` a plain string; these narrow it back
// to the enum instead of asserting, so a mistyped item value cannot sneak an
// arbitrary string into the wizard state.
function isCorrectionStrategy(value: string): value is CorrectionStrategy {
  return Object.values(CorrectionStrategy).some((s) => s === value);
}

function isValidationRuleTrigger(
  value: string,
): value is ValidationRuleTrigger {
  return Object.values(ValidationRuleTrigger).some((t) => t === value);
}

// ─── 1. Table / schema ───────────────────────────────────────────────────────

/**
 * The logical schema the rule is about. One card per scannable schema, with the
 * number of divisions the org holds it in — the breadth of what a rule over it
 * would compare.
 */
export function SchemaPanel({
  schemas,
  schemaName,
  onSelect,
  isLoading,
  loadError,
  editing,
}: {
  schemas: ValidationRuleSchemaDTO[];
  schemaName: string;
  onSelect: (name: string) => void;
  isLoading: boolean;
  loadError: Error | null;
  /** True while editing a saved rule, where changing tables is still allowed
   *  but clears the fields named in terms of the old one. */
  editing: boolean;
}) {
  if (loadError) {
    return (
      <p className="text-sm text-destructive">
        {/* The backend's message, rendered verbatim like an error toast. */}
        {loadError.message}
      </p>
    );
  }

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <SpinnerGapIcon className="h-4 w-4 animate-spin" />
        <T context="loading state while the rule builder fetches the schemas it can validate">
          Loading the tables you can validate…
        </T>
      </p>
    );
  }

  if (schemas.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
        <T context="empty state when no schema can be validated">
          No table here can be validated yet. A table needs an item-number
          column before its rows can be grouped by SKU.
        </T>
      </div>
    );
  }

  return (
    <WizardField
      name="schemaName"
      label={<T context="label for the validation rule table picker">Table</T>}
      hint={
        editing ? (
          <T context="hint when changing the table of a saved validation rule">
            Changing the table clears the fields and filters named in terms of
            the old one.
          </T>
        ) : undefined
      }
    >
      <div className="grid gap-2 sm:grid-cols-2">
        {schemas.map((schema) => {
          const selected = schema.name === schemaName;
          return (
            <button
              key={schema.schemaDefinitionId}
              type="button"
              aria-pressed={selected}
              onClick={() => onSelect(schema.name)}
              className={cn(
                'flex flex-col items-start gap-1 rounded-lg border p-3 text-left transition-colors',
                selected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50',
              )}
            >
              <span className="flex w-full items-center gap-2">
                <span className="flex-1 truncate font-mono text-sm font-medium">
                  {schema.label}
                </span>
                {selected && <CheckIcon className="h-4 w-4 text-primary" />}
              </span>
              <span className="text-xs text-muted-foreground">
                <T context="summary under a table option: how many divisions hold it and how many fields it has">
                  <Num>{schema.divisionCount}</Num> divisions ·{' '}
                  <Num>{schema.fields.length}</Num> fields
                </T>
              </span>
            </button>
          );
        })}
      </div>
    </WizardField>
  );
}

// ─── 2. Row filters ──────────────────────────────────────────────────────────

/**
 * Which rows the rule looks at. Reuses the catalog's advanced-filter editor —
 * `rowFilters` is the same `CatalogAdvancedFilter` shape the catalog sends — so
 * the operators and the AND semantics are identical to the grid the author
 * knows. The wizard unmounts an inactive panel, so revisiting the step re-seeds
 * the editor's draft rows from the filters already chosen.
 */
export function FiltersPanel({
  schema,
  rowFilters,
  onChange,
}: {
  schema: ValidationRuleSchemaDTO | null;
  rowFilters: CatalogAdvancedFilter[];
  onChange: (filters: CatalogAdvancedFilter[]) => void;
}) {
  const columns = (schema?.fields ?? []).map((field) => ({
    value: field.fieldName,
    label: field.fieldName,
    hint: field.label === field.fieldName ? null : field.label,
  }));

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        <T context="explanation of the optional row-filter step of the rule builder">
          Without a filter the rule reads every row of the table. Add
          constraints to narrow it — a rule about sellable items only, say.
        </T>
      </p>
      <AdvancedFilters
        columns={columns}
        includeBaseColumns={false}
        initialFilters={rowFilters}
        onApplyChange={onChange}
        hideTrigger
        open
      />
    </div>
  );
}

// ─── Field multi-select, shared by steps 3 and 4 ─────────────────────────────

/** A searchable multi-select over one schema's fields, with the picks as chips. */
function FieldMultiSelect({
  fields,
  selected,
  onToggle,
  /** Fields to mark as already used as grouping keys. */
  flaggedFields,
  flagLabel,
  placeholder,
}: {
  fields: ValidationSchemaField[];
  selected: string[];
  onToggle: (fieldName: string) => void;
  flaggedFields?: string[];
  flagLabel?: string;
  placeholder: string;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const selectedKeys = new Set(selected.map(fieldKey));
  const flaggedKeys = new Set((flaggedFields ?? []).map(fieldKey));

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            <span
              className={cn(
                'truncate',
                selected.length === 0 && 'text-muted-foreground',
              )}
            >
              {selected.length === 0
                ? placeholder
                : gt('{count} selected', {
                    context: 'trigger label of the field multi-select',
                    count: String(selected.length),
                  })}
            </span>
            <CaretDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-(--anchor-width) p-0" align="start">
          <Command>
            <CommandInput
              placeholder={gt('Search fields…', {
                context: 'placeholder of the field search box',
              })}
            />
            <CommandList>
              <CommandEmpty>
                <T context="empty state when no schema field matches the search">
                  No field found.
                </T>
              </CommandEmpty>
              <CommandGroup>
                {fields.map((field) => (
                  <CommandItem
                    key={field.fieldName}
                    value={`${field.fieldName} ${field.label}`}
                    onSelect={() => onToggle(field.fieldName)}
                  >
                    <CheckIcon
                      className={cn(
                        'mr-2 h-4 w-4 shrink-0',
                        selectedKeys.has(fieldKey(field.fieldName))
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                    />
                    <span className="min-w-0 flex-1 truncate font-mono text-xs">
                      {field.fieldName}
                    </span>
                    <FieldMarkers
                      field={field}
                      flagged={flaggedKeys.has(fieldKey(field.fieldName))}
                      flagLabel={flagLabel}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((fieldName) => (
            <Badge
              key={fieldName}
              variant={
                flaggedKeys.has(fieldKey(fieldName))
                  ? 'destructive'
                  : 'secondary'
              }
              className="gap-1 font-mono text-xs"
            >
              {fieldName}
              <button
                type="button"
                onClick={() => onToggle(fieldName)}
                aria-label={gt('Remove {field}', {
                  context: 'aria label to unselect a field chip',
                  field: fieldName,
                })}
              >
                <XIcon className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

/** The AS/400 role badges beside a field, plus the caller's own flag. */
function FieldMarkers({
  field,
  flagged,
  flagLabel,
}: {
  field: ValidationSchemaField;
  flagged: boolean;
  flagLabel?: string;
}) {
  return (
    <span className="ml-2 flex shrink-0 items-center gap-1">
      {field.isSkuColumn && (
        <Badge variant="outline" className="text-[10px]">
          <T context="badge marking the item-number field of a table">SKU</T>
        </Badge>
      )}
      {field.cdbRole && (
        <Badge variant="outline" className="text-[10px] capitalize">
          {field.cdbRole}
        </Badge>
      )}
      {flagged && flagLabel && (
        <Badge variant="destructive" className="text-[10px]">
          {flagLabel}
        </Badge>
      )}
    </span>
  );
}

// ─── 3. Grouping fields ──────────────────────────────────────────────────────

export function GroupingPanel({
  schema,
  groupingFields,
  onToggle,
}: {
  schema: ValidationRuleSchemaDTO | null;
  groupingFields: string[];
  onToggle: (fieldName: string) => void;
}) {
  const gt = useGT();

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        <T context="explanation of the grouping step: which rows are compared against each other">
          Rows that share these fields form one group, and the rule compares
          them against each other. Every division mapped to this table is
          evaluated — grouping by the item number alone compares that item
          across all of them.
        </T>
      </p>
      <WizardField
        name="groupingFields"
        label={
          <T context="label for the grouping-field multi-select">
            Group rows by
          </T>
        }
      >
        <FieldMultiSelect
          fields={schema?.fields ?? []}
          selected={groupingFields}
          onToggle={onToggle}
          placeholder={gt('Pick one or more fields', {
            context: 'placeholder of the grouping field multi-select',
          })}
        />
      </WizardField>
    </div>
  );
}

// ─── 4. Consistent fields ────────────────────────────────────────────────────

export function ConsistentFieldsPanel({
  schema,
  groupingFields,
  validatedFields,
  overlappingFields,
  onToggle,
}: {
  schema: ValidationRuleSchemaDTO | null;
  groupingFields: string[];
  validatedFields: string[];
  overlappingFields: string[];
  onToggle: (fieldName: string) => void;
}) {
  const gt = useGT();

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        <T context="explanation of the consistent-fields step">
          The rule flags a group when these fields do not agree across it. Pick
          at least one.
        </T>
      </p>
      <WizardField
        name="validatedFields"
        label={
          <T context="label for the consistent-field multi-select">
            Must agree
          </T>
        }
      >
        <FieldMultiSelect
          fields={schema?.fields ?? []}
          selected={validatedFields}
          onToggle={onToggle}
          flaggedFields={groupingFields}
          flagLabel={gt('grouping', {
            context: 'badge marking a field already used as a grouping key',
          })}
          placeholder={gt('Pick one or more fields', {
            context: 'placeholder of the consistent field multi-select',
          })}
        />
      </WizardField>

      {overlappingFields.length > 0 && (
        <p className="flex items-start gap-1.5 text-xs text-destructive">
          <WarningCircleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            <T context="inline error when a field is both grouped by and checked for agreement">
              <Var>{overlappingFields.join(', ')}</Var> already group the rows,
              so they agree within a group by definition. Remove them here, or
              stop grouping by them.
            </T>
          </span>
        </p>
      )}
    </div>
  );
}

// ─── 5. Normalization ────────────────────────────────────────────────────────

export function NormalizationPanel({
  normalization,
  onChange,
}: {
  normalization: ValidationRuleWizardState['normalization'];
  onChange: (flag: NormalizationFlag, value: boolean) => void;
}) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        <T context="explanation of the normalization step">
          How two cells are compared before the rule calls them different. All
          of this happens on the way into the comparison — the stored data is
          never touched.
        </T>
      </p>

      <NormalizationToggle
        flag="trimWhitespace"
        checked={normalization.trimWhitespace}
        onChange={onChange}
        label={
          <T context="normalization toggle label">
            Ignore leading and trailing spaces
          </T>
        }
        hint={
          <T context="normalization toggle hint for trimming whitespace">
            &quot; ACME &quot; counts as &quot;ACME&quot;.
          </T>
        }
      />
      <NormalizationToggle
        flag="caseInsensitive"
        checked={normalization.caseInsensitive}
        onChange={onChange}
        label={<T context="normalization toggle label">Ignore letter case</T>}
        hint={
          <T context="normalization toggle hint for case insensitivity">
            &quot;acme&quot; counts as &quot;ACME&quot;.
          </T>
        }
      />
      <NormalizationToggle
        flag="blankEqualsNull"
        checked={normalization.blankEqualsNull}
        onChange={onChange}
        label={
          <T context="normalization toggle label">Treat blank as missing</T>
        }
        hint={
          <T context="normalization toggle hint for blank equals null">
            An empty cell counts as no value at all, rather than as its own
            value.
          </T>
        }
      />
    </div>
  );
}

function NormalizationToggle({
  flag,
  checked,
  onChange,
  label,
  hint,
}: {
  flag: NormalizationFlag;
  checked: boolean;
  onChange: (flag: NormalizationFlag, value: boolean) => void;
  label: ReactNode;
  hint: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border p-3">
      <Switch
        id={`normalization-${flag}`}
        checked={checked}
        onCheckedChange={(value) => onChange(flag, value)}
      />
      <div className="space-y-0.5">
        <Label htmlFor={`normalization-${flag}`} className="text-sm">
          {label}
        </Label>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
    </div>
  );
}

// ─── 6. Correction strategy ──────────────────────────────────────────────────

export function StrategyPanel({
  correctionStrategy,
  onStrategyChange,
  divisions,
  preferredDataSourceId,
  onPreferredChange,
}: {
  correctionStrategy: CorrectionStrategy;
  onStrategyChange: (strategy: CorrectionStrategy) => void;
  divisions: WizardDivisionOption[];
  preferredDataSourceId: string;
  onPreferredChange: (id: string) => void;
}) {
  const gt = useGT();

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        <T context="explanation of the correction-strategy step">
          What the rule proposes for a group that disagrees. Every proposal
          carries the sentence explaining how it was picked, and a tie is
          reported as ambiguous rather than guessed at.
        </T>
      </p>

      <RadioGroup
        value={correctionStrategy}
        onValueChange={(value) => {
          if (isCorrectionStrategy(value)) onStrategyChange(value);
        }}
        className="gap-2"
      >
        <StrategyOption
          value={CorrectionStrategy.MostCommon}
          label={
            <T context="correction strategy option label">
              The value most rows carry
            </T>
          }
          hint={
            <T context="correction strategy option hint for most common">
              The majority wins. An even split is ambiguous.
            </T>
          }
        />
        <StrategyOption
          value={CorrectionStrategy.PreferredSource}
          label={
            <T context="correction strategy option label">
              The value one division carries
            </T>
          }
          hint={
            <T context="correction strategy option hint for preferred source">
              One division is the authority. Groups it has no row in are
              ambiguous.
            </T>
          }
        />
        <StrategyOption
          value={CorrectionStrategy.None}
          label={
            <T context="correction strategy option label">
              Flag it, propose nothing
            </T>
          }
          hint={
            <T context="correction strategy option hint for no strategy">
              The disagreement is reported and a human decides.
            </T>
          }
        />
      </RadioGroup>

      {correctionStrategy === CorrectionStrategy.PreferredSource && (
        <WizardField
          name="preferredDataSourceId"
          label={
            <T context="label for the preferred-division picker">
              Preferred division
            </T>
          }
          hint={
            divisions.length === 0 ? (
              <T context="hint when the selected table has no divisions to prefer">
                This table is not mapped to any division yet.
              </T>
            ) : undefined
          }
        >
          <Select
            value={preferredDataSourceId || undefined}
            onValueChange={onPreferredChange}
            disabled={divisions.length === 0}
          >
            <SelectTrigger className="w-full">
              <SelectValue
                placeholder={gt('Pick a division', {
                  context: 'placeholder of the preferred division picker',
                })}
              />
            </SelectTrigger>
            <SelectContent>
              {divisions.map((division) => (
                <SelectItem key={division.id} value={division.id}>
                  {division.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </WizardField>
      )}
    </div>
  );
}

function StrategyOption({
  value,
  label,
  hint,
}: {
  value: CorrectionStrategy;
  label: ReactNode;
  hint: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border p-3">
      <RadioGroupItem value={value} id={`strategy-${value}`} />
      <div className="space-y-0.5">
        <Label htmlFor={`strategy-${value}`} className="text-sm">
          {label}
        </Label>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
    </div>
  );
}

// ─── 7. Assignment and trigger ───────────────────────────────────────────────

export function AssignmentPanel({
  assigneeUserId,
  onAssigneeChange,
  executionTrigger,
  onTriggerChange,
  scheduleCron,
  onScheduleCronChange,
}: {
  assigneeUserId: string | null;
  onAssigneeChange: (userId: string | null) => void;
  executionTrigger: ValidationRuleTrigger;
  onTriggerChange: (trigger: ValidationRuleTrigger) => void;
  scheduleCron: string;
  onScheduleCronChange: (cron: string) => void;
}) {
  return (
    <div className="space-y-4">
      <WizardField
        name="assigneeUserId"
        label={
          <T context="label for the validation rule assignee picker">
            Assignee
          </T>
        }
        hint={
          <T context="hint under the validation rule assignee picker">
            Who owns what this rule flags. The reviews it stages land on their
            list.
          </T>
        }
      >
        <AssigneePicker
          value={assigneeUserId}
          onChange={onAssigneeChange}
          triggerId="validation-rule-assignee"
        />
      </WizardField>

      <div className="space-y-2">
        <Label>
          <T context="label for how a validation rule is triggered">
            Runs when
          </T>
        </Label>
        <RadioGroup
          value={executionTrigger}
          onValueChange={(value) => {
            if (isValidationRuleTrigger(value)) onTriggerChange(value);
          }}
          className="gap-2"
        >
          <TriggerOption
            value={ValidationRuleTrigger.Manual}
            label={<T context="rule trigger option label">Someone runs it</T>}
            hint={
              <T context="rule trigger option hint for manual runs">
                You run the rule from its page whenever you want a fresh look.
              </T>
            }
          />
          <TriggerOption
            value={ValidationRuleTrigger.Scheduled}
            label={<T context="rule trigger option label">On a schedule</T>}
            hint={
              <T context="rule trigger option hint for scheduled runs">
                The rule runs itself on a cron you set below.
              </T>
            }
          />
          <TriggerOption
            value={ValidationRuleTrigger.AfterIngest}
            label={
              <T context="rule trigger option label">
                After each data ingestion
              </T>
            }
            hint={
              <T context="rule trigger option hint for after-ingest runs">
                The rule runs whenever this table is reloaded, once the whole
                batch has landed.
              </T>
            }
          />
        </RadioGroup>
      </div>

      {executionTrigger === ValidationRuleTrigger.Scheduled && (
        <ScheduleCronField
          scheduleCron={scheduleCron}
          onScheduleCronChange={onScheduleCronChange}
        />
      )}
    </div>
  );
}

/**
 * The cron input, shown only for the scheduled trigger.
 *
 * A plain text field rather than a builder: the audience here already reads
 * cron (the workflows UI has used it for years) and the preview line below
 * carries the actual reassurance — it shows the next fire time the server will
 * compute from the same expression, so a typo is visible before saving rather
 * than at 6am tomorrow when nothing happened.
 */
function ScheduleCronField({
  scheduleCron,
  onScheduleCronChange,
}: {
  scheduleCron: string;
  onScheduleCronChange: (cron: string) => void;
}) {
  const nextRun = nextCronRun(scheduleCron);

  return (
    <WizardField
      name="scheduleCron"
      label={
        <T context="label for the validation rule cron expression input">
          Schedule
        </T>
      }
      hint={
        <T context="hint under the validation rule cron input">
          A five-field cron expression, read in UTC — for example “0 6 * * *”
          for every day at 06:00, or “0 6 * * 1” for Mondays at 06:00.
        </T>
      }
    >
      <div className="space-y-1.5">
        <Input
          id="validation-rule-schedule-cron"
          value={scheduleCron}
          onChange={(event) => onScheduleCronChange(event.target.value)}
          placeholder={CRON_EXAMPLE}
          className="font-mono"
          spellCheck={false}
          autoComplete="off"
        />
        {/*
          A bad cron is reported by the step's own validation through
          `WizardField`'s issue list, the same as every other step — no second
          inline copy of the message.
        */}
        {nextRun && (
          <p className="text-xs text-muted-foreground">
            <T context="preview of when a scheduled validation rule will next run">
              Next run
            </T>{' '}
            {formatScheduleTime(nextRun)}
          </p>
        )}
      </div>
    </WizardField>
  );
}

function TriggerOption({
  value,
  label,
  hint,
}: {
  value: ValidationRuleTrigger;
  label: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 rounded-lg border p-3">
      <RadioGroupItem value={value} id={`trigger-${value}`} />
      <div className="space-y-0.5">
        <Label htmlFor={`trigger-${value}`} className="text-sm">
          {label}
        </Label>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
    </div>
  );
}

// ─── 8. Preview ──────────────────────────────────────────────────────────────

export function PreviewPanel({
  preview,
  previewError,
  previewing,
  onRunPreview,
  hasPreviewed,
}: {
  preview: ValidationRuleWizardState['preview'];
  previewError: string | null;
  previewing: boolean;
  onRunPreview: () => void;
  /** True once a preview has run, so the button reads as a re-run. */
  hasPreviewed: boolean;
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          <T context="explanation of the preview step of the rule builder">
            A capped, read-only evaluation of the rule as it stands. Nothing is
            saved and no review is staged.
          </T>
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRunPreview}
          disabled={previewing}
        >
          {previewing ? (
            <SpinnerGapIcon className="h-4 w-4 animate-spin" />
          ) : (
            <ArrowClockwiseIcon className="h-4 w-4" />
          )}
          {hasPreviewed ? (
            <T context="button to evaluate the rule being authored again">
              Preview again
            </T>
          ) : (
            <T context="button to evaluate the rule being authored">
              Run preview
            </T>
          )}
        </Button>
      </div>

      {previewError && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <WarningCircleIcon
            className="mt-0.5 h-4 w-4 shrink-0"
            weight="fill"
          />
          {/* The server's own rejection, rendered verbatim: it names the
              author's fields, which no translation would improve. */}
          <span>{previewError}</span>
        </div>
      )}

      {previewing && !preview && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <SpinnerGapIcon className="h-4 w-4 animate-spin" />
          <T context="loading state while a validation rule preview runs">
            Evaluating the rule…
          </T>
        </p>
      )}

      {preview && <EvaluationResultView {...preview} />}
    </div>
  );
}

// ─── 9. Save ─────────────────────────────────────────────────────────────────

export function SavePanel({
  name,
  onNameChange,
  description,
  onDescriptionChange,
  activateOnSave,
  onActivateChange,
  canChooseStatus,
  saveError,
  violatingGroups,
}: {
  name: string;
  onNameChange: (name: string) => void;
  description: string;
  onDescriptionChange: (description: string) => void;
  activateOnSave: boolean;
  onActivateChange: (activate: boolean) => void;
  canChooseStatus: boolean;
  saveError: string | null;
  /** What the last preview found, for the line above the status choice. */
  violatingGroups: number | null;
}) {
  const gt = useGT();

  return (
    <div className="space-y-4">
      <WizardField
        name="name"
        label={<T context="label for the validation rule name">Name</T>}
        hint={
          <T context="hint under the validation rule name field">
            What this rule is called in the rules list and on the reviews it
            stages.
          </T>
        }
      >
        <Input
          value={name}
          onChange={(event) => onNameChange(event.target.value)}
          placeholder={gt('Descriptions must agree across divisions', {
            context: 'example validation rule name',
          })}
        />
      </WizardField>

      <WizardField
        name="description"
        label={
          <T context="label for the optional validation rule description">
            Description
          </T>
        }
      >
        <Textarea
          value={description}
          onChange={(event) => onDescriptionChange(event.target.value)}
          rows={2}
          placeholder={gt('Why this rule exists (optional)', {
            context: 'placeholder of the validation rule description field',
          })}
        />
      </WizardField>

      {violatingGroups !== null && (
        <p className="text-xs text-muted-foreground">
          <T context="reminder on the save step of what the preview found">
            The last preview flagged <Num>{violatingGroups}</Num> groups.
          </T>
        </p>
      )}

      {canChooseStatus ? (
        <div className="space-y-2">
          <Label>
            <T context="label for the save-as status choice">Save as</T>
          </Label>
          <RadioGroup
            value={activateOnSave ? 'active' : 'draft'}
            onValueChange={(value) => onActivateChange(value === 'active')}
            className="gap-2"
          >
            <div className="flex items-start gap-3 rounded-lg border p-3">
              <RadioGroupItem value="draft" id="save-as-draft" />
              <div className="space-y-0.5">
                <Label htmlFor="save-as-draft" className="text-sm">
                  <T context="save-as option label">Draft</T>
                </Label>
                <p className="text-xs text-muted-foreground">
                  <T context="save-as option hint for draft">
                    Saved but never run on its own. Activate it when you are
                    happy with what the preview shows.
                  </T>
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border p-3">
              <RadioGroupItem value="active" id="save-as-active" />
              <div className="space-y-0.5">
                <Label htmlFor="save-as-active" className="text-sm">
                  <T context="save-as option label">Active</T>
                </Label>
                <p className="text-xs text-muted-foreground">
                  <T context="save-as option hint for active">
                    Live: it can be run and it counts towards the open
                    violations on the rules list.
                  </T>
                </p>
              </div>
            </div>
          </RadioGroup>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          <T context="note on the save step when a rule's status is not editable here">
            This rule keeps the status it already has — pausing and activating
            happen on the rule&apos;s own page.
          </T>
        </p>
      )}

      {saveError && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          <WarningCircleIcon
            className="mt-0.5 h-4 w-4 shrink-0"
            weight="fill"
          />
          {/* The server's own rejection, rendered verbatim. */}
          <span>{saveError}</span>
        </div>
      )}
    </div>
  );
}
